import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  type PersistMigrate,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import sessionStorageEngine from 'redux-persist/lib/storage/session';
import { themeReducer } from '@kss/theme';
import { authReducer } from '@kss/auth';
import { sidebarReducer } from '@kss/layouts';

/**
 * Bump this whenever the desired-default of a persisted slice changes
 * (e.g. swapping the storefront from light/default to dark/blue). The
 * `migrate` map below resets stale slices on rehydrate so existing
 * users pick up the new defaults without having to clear localStorage.
 */
const PERSIST_VERSION = 2;

interface MigrationState {
  theme?: { mode?: string; colorTheme?: string; colorMode?: string };
  auth?: unknown;
}

/* Per-slice persist config so theme / sidebar survive a browser close
   (they're harmless UI preferences) but the **auth slice dies with
   the tab** — sessionStorage is scoped to the active browsing
   context, so closing the tab or quitting the browser clears the
   token automatically. Bug ref: AI Agent 2026-05-14 — "when browser
   tab close or browser close token should be expired". */
const persistConfigRoot = {
  key: 'kss-web',
  storage,
  version: PERSIST_VERSION,
  whitelist: ['theme'],
  migrate: createMigrate(
    {
      2: (state) => {
        if (!state) return state;
        const next = { ...(state as unknown as MigrationState) };
        delete next.theme;
        return next as typeof state;
      },
    },
    { debug: false },
  ) as PersistMigrate,
};

const authPersistConfig = {
  key: 'kss-web-auth',
  storage: sessionStorageEngine,
  // No whitelist needed — the auth slice IS the entire reducer.
};

const rootReducer = combineReducers({
  theme: themeReducer,
  sidebar: sidebarReducer,
  // Wrap the auth slice in its own persistReducer so it goes to
  // sessionStorage while everything else uses localStorage.
  auth: persistReducer(authPersistConfig, authReducer),
});

const persistedReducer = persistReducer(persistConfigRoot, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
